import { useEffect, useRef } from 'react'

/**
 * Decorative chrome for authenticated pages — sits behind the navbar and
 * page content. Three fixed, pointer-events:none layers:
 *
 *   1. Background image (the building blueprint photo, cover, fixed)
 *   2. A side-masked navy dot grid that fades behind centered content
 *   3. A cursor-following cyan dot spotlight (intersects with the same
 *      side mask, driven by two CSS vars updated inside rAF)
 *
 * The pattern matches the locked design (see REDESIGN_INTEGRATION §4) and
 * is shared across every redesigned authenticated page.
 */
export function PageChrome() {
  const spotlightRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = spotlightRef.current
    if (!el) {
      return
    }

    let pending = false
    let x = 0
    let y = 0

    function onMove(e: MouseEvent) {
      x = e.clientX
      y = e.clientY
      if (pending) {
        return
      }
      pending = true
      requestAnimationFrame(() => {
        el!.style.setProperty('--mx', `${x}px`)
        el!.style.setProperty('--my', `${y}px`)
        pending = false
      })
    }

    window.addEventListener('mousemove', onMove, { passive: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  return (
    <>
      {/* Layer 1 — building photo background, fixed, with soft white tint. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 bg-[url('/image/background.png')] bg-cover bg-fixed bg-top after:absolute after:inset-0 after:bg-[linear-gradient(180deg,rgba(244,246,251,0.55)_0%,rgba(244,246,251,0.78)_100%)] after:content-['']"
      />

      {/* Layer 2 — side-masked navy dot grid. */}
      <div
        aria-hidden="true"
        className="page-dot-grid pointer-events-none fixed inset-0 z-0"
      />

      {/* Layer 3 — cursor-following cyan spotlight (intersects with the side mask). */}
      <div
        ref={spotlightRef}
        aria-hidden="true"
        className="page-dot-spotlight pointer-events-none fixed inset-0 z-0"
      />
    </>
  )
}
